import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Gem, Sparkles, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { login } from "@/lib/api";
import { toast } from "sonner";

export default function Gems() {
  const { user } = useAuth();
  const [packs, setPacks] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/gems/packs").then(r => setPacks(r.data)).catch(() => {});
  }, []);

  const buy = async (lookup_key) => {
    if (!user) { login(); return; }
    setBusy(lookup_key);
    try {
      const { data } = await api.post("/payments/gems", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setBusy("");
    }
  };

  const perks = [
    "Unlock Steamy Mode on any companion (24h window)",
    "Spin a chat into a 4-chapter story with cover art",
    "Unlock rare & legendary characters forever",
    "Character voice replies & share clips",
    "Send a Steamy session as a gift link to a friend",
    "Processing fees are passed to you — you keep the bonus gems",
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 md:py-20">
      <div className="text-center mb-14">
        <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/5 px-3 py-1.5 mb-6">
          <Gem className="h-3.5 w-3.5 text-amber-400" />
          <span className="font-mono text-[11px] uppercase tracking-[0.24em] text-amber-300">Gems</span>
        </div>
        <h1 className="font-serif text-3xl md:text-5xl font-bold tracking-tight">
          Top up your <span className="italic text-amber-400">Gems</span>
        </h1>
        <p className="mt-4 text-slate-400 max-w-xl mx-auto">Gems unlock premium reader features so casual readers can pay only for what they use — instead of committing to a full subscription. We pass processing fees to you and give you <span className="text-amber-300 font-medium">bonus gems</span> on larger packs as a thank-you.</p>
        {user && (
          <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-[#0F1117] px-4 py-2">
            <Gem className="h-4 w-4 text-amber-400" />
            <span className="font-mono text-sm text-amber-300 font-semibold">{user.credits}</span>
            <span className="font-mono text-[11px] uppercase tracking-widest text-slate-500">on hand</span>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-5 md:gap-6 mb-14">
        {packs.map((p, i) => (
          <div
            key={p.lookup_key}
            data-testid={`pack-${p.lookup_key}`}
            className={`relative rounded-3xl p-8 border ${i === 1 ? "border-amber-500/60 bg-gradient-to-b from-amber-500/10 to-transparent" : "border-white/10 bg-[#0F1117]"}`}
          >
            {i === 1 && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 contest-pill text-[9px]" style={{background: "linear-gradient(90deg, #F59E0B, #F97316)"}}>
                Best value
              </div>
            )}
            <div className="flex items-center gap-2 mb-4">
              <Gem className={`h-5 w-5 ${i === 0 ? "text-amber-300" : i === 1 ? "text-amber-400" : "text-rose-400"}`} />
              <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-slate-500">{p.label}</span>
            </div>
            <div className="flex items-baseline gap-1 mb-1">
              <span className="font-serif text-5xl font-bold">{p.gems}</span>
              <span className="text-slate-500">gems</span>
            </div>
            {p.lookup_key === "gems_500" && (
              <div className="font-mono text-[10px] uppercase tracking-widest text-amber-400">+50 bonus · 10% back</div>
            )}
            {p.lookup_key === "gems_1500" && (
              <div className="font-mono text-[10px] uppercase tracking-widest text-amber-400">+250 bonus · 16% back</div>
            )}
            <div className="text-sm text-slate-400 mb-6 mt-1">${p.price_usd?.toFixed(2) ?? "—"} one-time · fees passed to you</div>
            <Button
              data-testid={`buy-${p.lookup_key}`}
              onClick={() => buy(p.lookup_key)}
              disabled={busy === p.lookup_key}
              className={`w-full h-11 rounded-full font-semibold ${i === 1 ? "bg-amber-500 hover:bg-amber-400 text-black" : "bg-white/8 hover:bg-white/12 text-white"}`}
            >
              {busy === p.lookup_key ? "Redirecting…" : "Buy pack"}
            </Button>
          </div>
        ))}
      </div>

      <div className="rounded-3xl border border-white/10 bg-[#0F1117] p-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-amber-400 mb-3 flex items-center gap-2"><Sparkles className="h-3 w-3" /> What gems unlock</div>
        <ul className="grid sm:grid-cols-2 gap-3">
          {perks.map(p => (
            <li key={p} className="flex items-start gap-2 text-sm text-slate-300">
              <Check className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" /> {p}
            </li>
          ))}
        </ul>
        <p className="mt-5 text-xs text-slate-500 font-mono uppercase tracking-widest">Gems are non-refundable and never expire</p>
      </div>
    </div>
  );
}
